import { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import styled from '@emotion/styled';
import Hls from 'hls.js';

/**
 * VideoStoryPlayer — lecteur vidéo style Instagram Stories.
 *
 * Props :
 *   videos     : [{ url, type }] — flux HLS depuis tutorialVideos
 *   onIndexChange : callback appelé quand l'index change (idx, isLast)
 * 
 * Méthodes exposées via ref :
 *   goNext() : avancer à la vidéo suivante
 *   goPrev() : reculer à la vidéo précédente
 */
const VideoStoryPlayer = forwardRef(({
  videos = [],
  onIndexChange,
}, ref) => {
  const [idx, setIdx] = useState(0);
  const [progress, setProgress] = useState(0);
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const rafRef = useRef(null);

  const goNext = useCallback(() => {
    if (idx < videos.length - 1) {
      setIdx(i => i + 1);
    }
  }, [idx, videos.length]);

  const goPrev = useCallback(() => {
    setIdx(i => Math.max(0, i - 1));
  }, []);

  // Exposer les méthodes au parent via ref
  useImperativeHandle(ref, () => ({
    goNext,
    goPrev,
  }), [goNext, goPrev]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !videos[idx]) return;

    setProgress(0);
    cancelAnimationFrame(rafRef.current);

    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    const { url } = videos[idx];

    function tick() {
      if (video.duration > 0) {
        setProgress(video.currentTime / video.duration);
      }
      rafRef.current = requestAnimationFrame(tick);
    }

    if (Hls.isSupported()) {
      const hls = new Hls();
      hlsRef.current = hls;
      hls.loadSource(url);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        video.play().catch(() => {});
        rafRef.current = requestAnimationFrame(tick);
      });
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = url;
      video.play().catch(() => {});
      rafRef.current = requestAnimationFrame(tick);
    }

    return () => {
      cancelAnimationFrame(rafRef.current);
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [idx, videos]);

  // Notifier le parent du changement d'index
  useEffect(() => {
    const isLast = idx === videos.length - 1;
    onIndexChange?.(idx, isLast);
  }, [idx, videos.length, onIndexChange]);

  const isLast = idx === videos.length - 1;

  // Ne pas avancer automatiquement à la fin de la dernière vidéo
  const handleVideoEnded = useCallback(() => {
    if (!isLast) {
      goNext();
    }
  }, [isLast, goNext]);

  if (!videos.length) return null;

  return (
    <Wrapper>
      <Video ref={videoRef} playsInline muted onEnded={handleVideoEnded} />
      <TopGradient />
      <BottomGradient />

      <ProgressBars>
        {videos.map((_, i) => (
          <Track key={i}>
            <Fill $pct={i < idx ? 1 : i === idx ? progress : 0} />
          </Track>
        ))}
      </ProgressBars>

      <TapZone onClick={goPrev} $side="left" aria-label="Vidéo précédente" />
      <TapZone onClick={goNext} $side="right" aria-label="Vidéo suivante" />
    </Wrapper>
  );
});

VideoStoryPlayer.displayName = 'VideoStoryPlayer';

export default VideoStoryPlayer;

const Wrapper = styled.div`
  position: relative;
  max-width: 100%;
  height: 100%;
  aspect-ratio: 9 / 16;
  margin: 0 auto;
  border-radius: 16px;
  overflow: hidden;
  background: #000;
`;

const Video = styled.video`
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
`;

const TopGradient = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 80px;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.55), transparent);
  pointer-events: none;
  z-index: 1;
`;

const BottomGradient = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 130px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.65), transparent);
  pointer-events: none;
  z-index: 1;
`;

const ProgressBars = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
  right: 12px;
  display: flex;
  gap: 4px;
  z-index: 2;
`;

const Track = styled.div`
  flex: 1;
  height: 3px;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.35);
  overflow: hidden;
`;

const Fill = styled.div`
  height: 100%;
  width: ${p => Math.round(p.$pct * 100)}%;
  background: #fff;
  transition: width 0.1s linear;
`;

const TapZone = styled.button`
  position: absolute;
  top: 0;
  ${p => p.$side}: 0;
  bottom: 80px;
  width: 35%;
  background: transparent;
  border: none;
  cursor: pointer;
  z-index: 3;
  -webkit-tap-highlight-color: transparent;
`;
